import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as messagingApi from '@/features/messaging/api';
import type { MessageView, SendMessageInput } from '@/features/messaging/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';
import type { PagedData } from '@/lib/api/types';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/** Tenant-scoped query key embeds the Active Organization id AND the student id (ADR-026 Addendum, AO-9). */
const messagesListKey = (organizationId: string | null, studentId: string) =>
  [TENANT_QUERY_PREFIX, organizationId, 'messages', studentId] as const;

export function useMessages(studentId: string): UseQueryResult<PagedData<MessageView>, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: messagesListKey(activeOrganizationId, studentId),
    queryFn: () => messagingApi.listMessages(studentId),
    enabled: activeOrganizationId !== null && studentId.length > 0,
  });
}

export function useSendMessage(
  studentId: string,
): UseMutationResult<MessageView, ApiError, SendMessageInput> {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useActiveOrganization();
  return useMutation({
    mutationFn: (input: SendMessageInput) => messagingApi.sendMessage(studentId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: messagesListKey(activeOrganizationId, studentId),
      });
    },
  });
}
